/**
 * EmployeeVerify.js — Public Employee ID Verification Page
 * Route: /verify/:uuid (PUBLIC — no auth, no PrivateRoute wrapper)
 *
 * Destination of the QR code on EmployeeIdCard.js. A security guard,
 * building reception, or vendor can scan an employee's badge and land
 * here directly — no PeoplesDock login needed — to confirm the person
 * is a real, currently-active employee.
 *
 * Shows ONLY non-sensitive fields (name, photo, designation,
 * department, company) plus a clear active/inactive badge. The badge
 * is the actual security value: it's computed live from the
 * employee's current status, so an old printed/saved ID card
 * automatically stops validating the moment someone is offboarded —
 * no manual revocation or card reprinting needed.
 */
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Container, Card, Spinner, Alert } from 'react-bootstrap';
import { CheckCircle, XCircle, Building2 } from 'lucide-react';
import axiosInstance from '../../api/axiosConfig';

const companyInitials = (name = '') =>
    (name || 'CO').split(' ').slice(0, 2).map(w => w[0]?.toUpperCase() || '').join('') || 'CO';

const EmployeeVerify = () => {
    const { uuid } = useParams();
    const [data,    setData]    = useState(null);
    const [loading, setLoading] = useState(true);
    const [error,   setError]   = useState('');

    useEffect(() => {
        if (!uuid) return;
        axiosInstance.get(`/employees/public/verify/${uuid}`)
            .then(res => setData(res.data.data))
            .catch(err => setError(err.response?.data?.message || 'Could not verify this ID card.'))
            .finally(() => setLoading(false));
    }, [uuid]);

    const fullName = data ? [data.first_name, data.last_name].filter(Boolean).join(' ') : '';
    const initials = data
        ? [data.first_name?.[0], data.last_name?.[0]].filter(Boolean).join('').toUpperCase() || 'EM'
        : '';

    return (
        <div style={{
            minHeight: '100vh',
            background: 'linear-gradient(135deg,#f0f4ff 0%,#faf5ff 50%,#f0fdf4 100%)',
            display: 'flex', alignItems: 'center', padding: '24px 16px',
        }}>
            <Container style={{ maxWidth: 420 }}>
                <Card className="border-0 shadow-sm" style={{ borderRadius: 20, overflow: 'hidden' }}>
                    <Card.Body className="p-0">
                        {loading ? (
                            <div className="text-center py-5">
                                <Spinner animation="border" variant="primary"/>
                                <p className="text-muted small mt-3 mb-0">Verifying…</p>
                            </div>
                        ) : error || !data ? (
                            <div className="p-4">
                                <Alert variant="danger" className="d-flex align-items-center gap-2 mb-0">
                                    <XCircle size={20}/>
                                    <span>{error || 'No employee found for this ID card.'}</span>
                                </Alert>
                            </div>
                        ) : (
                            <>
                                {/* Company branding strip */}
                                <div style={{
                                    background: 'linear-gradient(135deg,#4f46e5,#7c3aed)',
                                    padding: '20px 24px',
                                    display: 'flex', alignItems: 'center', gap: 10,
                                }}>
                                    {data.company_logo_url ? (
                                        <img src={data.company_logo_url} alt={data.company_name}
                                            style={{ height: 28, width: 'auto', maxWidth: 100, objectFit: 'contain' }}/>
                                    ) : (
                                        <Building2 size={20} color="#fff"/>
                                    )}
                                    <span style={{ color: '#fff', fontWeight: 700, fontSize: 14 }}>
                                        {data.company_name}
                                    </span>
                                </div>

                                {/* Photo + name + role */}
                                <div className="text-center" style={{ padding: '28px 24px 20px' }}>
                                    {data.avatar ? (
                                        <img src={data.avatar} alt={fullName}
                                            style={{
                                                width: 96, height: 96, borderRadius: '50%',
                                                objectFit: 'cover', margin: '0 auto 14px',
                                                border: '4px solid #eef2ff', display: 'block',
                                            }}/>
                                    ) : (
                                        <div style={{
                                            width: 96, height: 96, borderRadius: '50%',
                                            background: '#eef2ff', margin: '0 auto 14px',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            fontSize: 32, fontWeight: 800, color: '#4f46e5',
                                            border: '4px solid #eef2ff',
                                        }}>
                                            {initials}
                                        </div>
                                    )}
                                    <div style={{ fontSize: 20, fontWeight: 800, color: '#0f172a' }}>
                                        {fullName}
                                    </div>
                                    <div style={{ fontSize: 14, color: '#64748b', marginTop: 2 }}>
                                        {data.designation_name || 'Employee'}
                                    </div>
                                    <div style={{ fontSize: 13, color: '#94a3b8', marginTop: 1 }}>
                                        {data.department_name || ''}
                                    </div>
                                    {data.employee_code && (
                                        <div style={{
                                            display: 'inline-block', marginTop: 10,
                                            fontSize: 12, fontWeight: 700, color: '#4f46e5',
                                            background: '#eef2ff', borderRadius: 20,
                                            padding: '4px 14px', letterSpacing: 0.5,
                                        }}>
                                            {data.employee_code}
                                        </div>
                                    )}
                                </div>

                                {/* The actual security feature — live active/inactive badge */}
                                <div style={{
                                    margin: '0 24px 24px',
                                    borderRadius: 14,
                                    padding: '16px 18px',
                                    background: data.is_active ? '#f0fdf4' : '#fef2f2',
                                    border: `1px solid ${data.is_active ? '#bbf7d0' : '#fecaca'}`,
                                    display: 'flex', alignItems: 'center', gap: 10,
                                }}>
                                    {data.is_active ? (
                                        <CheckCircle size={22} color="#059669"/>
                                    ) : (
                                        <XCircle size={22} color="#dc2626"/>
                                    )}
                                    <div>
                                        <div style={{
                                            fontSize: 14, fontWeight: 700,
                                            color: data.is_active ? '#065f46' : '#991b1b',
                                        }}>
                                            {data.is_active ? 'Verified Active Employee' : 'Not Currently Active'}
                                        </div>
                                        <div style={{
                                            fontSize: 11,
                                            color: data.is_active ? '#15803d' : '#b91c1c',
                                        }}>
                                            {data.is_active
                                                ? `Confirmed employee of ${data.company_name}`
                                                : 'This ID card is no longer valid'}
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                    </Card.Body>
                </Card>
                <p className="text-center text-muted small mt-3 mb-0">
                    Verified via PeoplesDock
                </p>
            </Container>
        </div>
    );
};

export default EmployeeVerify;
